import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import RichTextEditor from "components/RichTextEditor";
import FeaturedImagePicker from "components/content/FeaturedImagePicker";
import AuthorSelect from "components/content/AuthorSelect";
import CategorySelect from "components/content/CategorySelect";
import TagPicker from "components/content/TagPicker";
import { slugify } from "components/content/contentUtils";
import { listForms } from "api/endpoints/forms";

const SectionLabel = ({ children }) => (
  <MDTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
    {children}
  </MDTypography>
);
SectionLabel.propTypes = { children: PropTypes.node.isRequired };

/**
 * Batch 2.3 — <ContentBaseFields>. The shared base of every content form:
 * title, slug, excerpt, body, featured image, author, categories, tags, SEO
 * and gating. Controlled by `value` (the base state) / `onChange` (a patch).
 */
function ContentBaseFields({ value, onChange, entry, websiteId, categories }) {
  const [forms, setForms] = useState([]);
  // Once the slug has been hand-edited, stop deriving it from the title.
  const [slugTouched, setSlugTouched] = useState(() => Boolean(value.slug));

  // Quirk #2 — the slug is immutable once the content is published.
  const slugLocked = value.status === "published";
  const gatedForced = entry.gated === "forced";

  useEffect(() => {
    if (!websiteId) return;
    listForms(websiteId)
      .then((res) => setForms(res.items))
      .catch(() => setForms([]));
  }, [websiteId]);

  const handleTitle = (event) => {
    const title = event.target.value;
    const patch = { title };
    if (!slugTouched && !slugLocked) patch.slug = slugify(title);
    onChange(patch);
  };

  const handleSlug = (event) => {
    setSlugTouched(true);
    onChange({ slug: slugify(event.target.value) });
  };

  return (
    <Stack spacing={3}>
      <TextField label="Title" value={value.title} onChange={handleTitle} fullWidth required />

      <TextField
        label="Slug"
        value={value.slug}
        onChange={handleSlug}
        fullWidth
        disabled={slugLocked}
        helperText={slugLocked ? "The slug cannot change once the content is published" : undefined}
      />

      <TextField
        label="Excerpt"
        value={value.excerpt}
        onChange={(e) => onChange({ excerpt: e.target.value })}
        fullWidth
        multiline
        minRows={2}
        helperText="Plain text — used in listings and link previews"
      />

      <MDBox>
        <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
          Body
        </MDTypography>
        <RichTextEditor
          value={value.body}
          onChange={(html) => onChange({ body: html })}
          websiteId={websiteId}
          placeholder="Write the content…"
        />
      </MDBox>

      <FeaturedImagePicker
        value={value.featured_image_id}
        onChange={(id) => onChange({ featured_image_id: id })}
        websiteId={websiteId}
      />

      {entry.author && (
        <AuthorSelect
          value={value.author_id}
          onChange={(id) => onChange({ author_id: id })}
          websiteId={websiteId}
          required
        />
      )}

      <CategorySelect
        value={value.category_ids}
        onChange={(ids) => onChange({ category_ids: ids })}
        categories={categories}
      />

      <TagPicker
        value={value.tag_ids}
        onChange={(ids) => onChange({ tag_ids: ids })}
        websiteId={websiteId}
      />

      <Divider />
      <SectionLabel>SEO</SectionLabel>
      <TextField
        label="SEO title"
        value={value.seo_title}
        onChange={(e) => onChange({ seo_title: e.target.value })}
        fullWidth
      />
      <TextField
        label="SEO description"
        value={value.seo_description}
        onChange={(e) => onChange({ seo_description: e.target.value })}
        fullWidth
        multiline
        minRows={2}
      />

      <Divider />
      <SectionLabel>Access</SectionLabel>
      {gatedForced ? (
        <MDTypography variant="button" color="text">
          This content type is always gated — visitors must submit the form below to access it.
        </MDTypography>
      ) : (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value.is_gated)}
              onChange={(e) => onChange({ is_gated: e.target.checked })}
            />
          }
          label="Gated — require a form submission to view"
        />
      )}

      {(value.is_gated || gatedForced) && (
        <TextField
          select
          label="Gating form"
          value={value.gating_form_id || ""}
          onChange={(e) => onChange({ gating_form_id: e.target.value })}
          fullWidth
          required={gatedForced}
          helperText={forms.length === 0 ? "No forms defined for this website" : undefined}
        >
          <MenuItem value="" disabled>
            Select a form
          </MenuItem>
          {forms.map((form) => (
            <MenuItem key={form.form_id} value={form.form_id}>
              {form.name}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}

ContentBaseFields.defaultProps = {
  websiteId: undefined,
  categories: [],
};

ContentBaseFields.propTypes = {
  value: PropTypes.shape({
    title: PropTypes.string,
    slug: PropTypes.string,
    excerpt: PropTypes.string,
    body: PropTypes.string,
    featured_image_id: PropTypes.string,
    author_id: PropTypes.string,
    category_ids: PropTypes.arrayOf(PropTypes.string),
    tag_ids: PropTypes.arrayOf(PropTypes.string),
    seo_title: PropTypes.string,
    seo_description: PropTypes.string,
    is_gated: PropTypes.bool,
    gating_form_id: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  entry: PropTypes.shape({
    author: PropTypes.bool,
    gated: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }).isRequired,
  websiteId: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.object),
};

export default ContentBaseFields;
